import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useMpesaTransactionPoll } from '@/hooks/use-payments'
import { getApiErrorMessage } from '@/lib/api'
import { cn, formatKES } from '@/lib/utils'

const POLL_TIMEOUT_MS = 60_000
const PHONE_PATTERN = /^\+254\d{9}$/

export interface PayNowResult {
  transactionId: string
  amount: number
}

export interface PayNowFlowProps {
  /** Current cumulative outstanding balance — this is always what gets charged. */
  amount: number
  /** Member's registered phone — prepopulates the editable STK field. */
  defaultPhone: string
  paybill: string
  accountReference: string
  onPay: (phone: string) => Promise<PayNowResult>
  isPaying?: boolean
  /**
   * Live status polling requires a logged-in session (GET /mpesa/transactions/:id is
   * auth-gated) — the no-login quick-pay page can't use it. Defaults to true; the quick-pay
   * page passes false and shows a static confirmation instead (an SMS confirms completion
   * either way once M-Pesa responds).
   */
  pollStatus?: boolean
}

type Method = 'mpesa' | 'paybill'

function MethodOption({
  selected,
  onSelect,
  title,
  description,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  description: string
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded border px-3 py-2.5 transition-colors',
        selected ? 'border-primary bg-cream-2/50' : 'border-border-2 hover:bg-cream-2/30',
      )}
    >
      <input
        type="radio"
        name="pay-now-method"
        checked={selected}
        onChange={onSelect}
        className="mt-1"
      />
      <span className="min-w-0 font-sans text-sm">
        <span className="block font-medium text-ink">{title}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
    </label>
  )
}

/** Shared "Pay now" button + M-Pesa flow — always charges the member's full outstanding
 * balance, never a single invoice's amount. Used on the Bills page, an invoice's own detail
 * page, and the no-login email quick-pay page. Opens a modal offering a choice between an STK
 * push (editable phone, defaulting to the registered number) and paying the Paybill directly. */
export function PayNowFlow({
  amount,
  defaultPhone,
  paybill,
  accountReference,
  onPay,
  isPaying,
  pollStatus = true,
}: PayNowFlowProps) {
  const [open, setOpen] = useState(false)
  const [method, setMethod] = useState<Method>('mpesa')
  const [phone, setPhone] = useState(defaultPhone)
  const [copied, setCopied] = useState(false)
  const [phoneError, setPhoneError] = useState<string | null>(null)

  const [transactionId, setTransactionId] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [chargedAmount, setChargedAmount] = useState(amount)
  const [error, setError] = useState<string | null>(null)
  const [posted, setPosted] = useState(false)

  const poll = useMpesaTransactionPoll(pollStatus ? transactionId : null, startedAt)
  const status = pollStatus ? poll.data?.data.status : undefined

  function handleOpen() {
    setMethod('mpesa')
    setPhone(defaultPhone)
    setPhoneError(null)
    setOpen(true)
  }

  function handleClose() {
    setOpen(false)
    setTransactionId(null)
    setStartedAt(null)
    setPosted(false)
    setError(null)
  }

  function handleRetry() {
    setTransactionId(null)
    setStartedAt(null)
    setPosted(false)
    setError(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!PHONE_PATTERN.test(phone)) {
      setPhoneError('Enter a valid phone number, e.g. +254712345678')
      return
    }
    setPhoneError(null)
    setError(null)
    try {
      const result = await onPay(phone)
      setTransactionId(result.transactionId)
      setChargedAmount(result.amount)
      setStartedAt(Date.now())
      setPosted(true)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to start M-Pesa payment'))
    }
  }

  async function handleCopyAccountReference() {
    await navigator.clipboard.writeText(accountReference)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function renderStatus() {
    if (!pollStatus) {
      return (
        <p className="rounded border border-border-2 bg-cream-2 px-4 py-3 font-sans text-sm text-ink">
          Check your phone for the M-Pesa prompt for {formatKES(chargedAmount)}… You&apos;ll get an
          SMS once it&apos;s confirmed.
        </p>
      )
    }

    const timedOut = status === 'pending' && !!startedAt && Date.now() - startedAt > POLL_TIMEOUT_MS

    if (status === 'successful') {
      return (
        <p className="rounded border border-border-2 bg-cream-2 px-4 py-3 font-sans text-sm text-primary">
          Payment received — thank you.
        </p>
      )
    }
    if (status === 'failed' || status === 'cancelled') {
      return (
        <div className="space-y-2">
          <p className="font-sans text-sm text-destructive">
            {poll.data?.data.resultDescription ?? 'Payment was not completed.'}
          </p>
          <Button type="button" onClick={handleRetry}>
            Try again
          </Button>
        </div>
      )
    }
    if (timedOut) {
      return (
        <p className="rounded border border-border-2 bg-cream-2 px-4 py-3 font-sans text-sm text-muted-foreground">
          Still processing — we&apos;ll update this once M-Pesa confirms. Refresh this page in a
          moment if you don&apos;t see it reflected.
        </p>
      )
    }
    return (
      <p className="rounded border border-border-2 bg-cream-2 px-4 py-3 font-sans text-sm text-ink">
        Check your phone for the M-Pesa prompt for {formatKES(chargedAmount)}…
      </p>
    )
  }

  return (
    <>
      <Button onClick={handleOpen}>Pay {formatKES(amount)} now</Button>

      <Modal open={open} onClose={handleClose} labelledBy="pay-now-title">
        <ModalHeader>
          <h2 id="pay-now-title" className="font-serif text-xl text-ink">
            Pay {formatKES(amount)}
          </h2>
        </ModalHeader>

        {posted ? (
          <>
            <ModalBody>{renderStatus()}</ModalBody>
            <ModalFooter>
              <Button type="button" onClick={handleClose}>
                Close
              </Button>
            </ModalFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <ModalBody className="space-y-4">
              <div className="space-y-2">
                <MethodOption
                  selected={method === 'mpesa'}
                  onSelect={() => setMethod('mpesa')}
                  title="M-Pesa"
                  description="Get an STK prompt on your phone"
                />
                <MethodOption
                  selected={method === 'paybill'}
                  onSelect={() => setMethod('paybill')}
                  title="Pay via Paybill directly"
                  description="Pay from your own M-Pesa menu"
                />
              </div>

              {method === 'mpesa' ? (
                <>
                  <Input
                    label="M-Pesa phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+254712345678"
                    hint="You can pay from a different number than the one on file"
                    error={phoneError ?? undefined}
                  />
                  {error && (
                    <p className="font-sans text-xs text-destructive" role="alert">
                      {error}
                    </p>
                  )}
                </>
              ) : (
                <div className="space-y-2 rounded border border-border-2 bg-cream-2 px-4 py-3">
                  <div className="flex items-center justify-between font-sans text-sm">
                    <span className="text-muted-foreground">Paybill</span>
                    <span className="font-medium text-ink">{paybill}</span>
                  </div>
                  <div className="flex items-center justify-between font-sans text-sm">
                    <span className="text-muted-foreground">Account number</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-ink">{accountReference}</span>
                      <button
                        type="button"
                        onClick={() => void handleCopyAccountReference()}
                        aria-label="Copy account number"
                        className="text-muted-foreground hover:text-ink"
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                  <p className="pt-1 font-sans text-xs text-muted-foreground">
                    Paying this way is usually detected and applied automatically within a few
                    minutes. If it isn&apos;t, our office will pick it up and apply it shortly.
                  </p>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button type="button" variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              {method === 'mpesa' && (
                <Button type="submit" loading={isPaying}>
                  Pay {formatKES(amount)} now
                </Button>
              )}
            </ModalFooter>
          </form>
        )}
      </Modal>
    </>
  )
}
